// Input: keyboard, mouse-aim cursor, gamepads, and HOTAS sticks.
//
// Keyboard keys set a target of ±1 and the control surface slews toward it
// (spring-centered-stick feel). Gamepads/HOTAS provide analog axes directly.
// Mouse aim exposes a virtual cursor; main.js turns it into steering via the
// flight-assist controller and feeds the result back through setAssist().
//
// Device profiles follow the original prototype:
//   - Standard gamepads (Xbox layout, War-Thunder style): left stick
//     roll/pitch, right stick X rudder, right stick Y throttle rate,
//     A or RT fires, B drops bombs, LB/RB step the throttle.
//   - HOTAS: axes 0/1/2/3 = roll/pitch/twist-yaw/throttle (absolute,
//     forward = full), button 0 (trigger) fires, button 1 drops bombs.
//     Overridable via the "va-hotas-map" localStorage key.
//
// The KEYBOARD bindings are not hardcoded here anymore — they live in the
// remappable keymap (keybindings.js), which Settings > Keybindings edits and
// persists. We read the live keymap every frame, so a rebind bites immediately.

import { keymap, shouldPreventDefault, isCapturing } from "./keybindings.js";

const HOTAS_DEVICE_PATTERNS = [
  /thrustmaster/i, /t\.?16000/i, /t\.?flight/i, /hotas/i, /warthog/i, /twcs/i,
  /x52/i, /x55/i, /x56/i, /saitek/i, /logitech.*flight/i, /logitech.*stick/i,
  /vkb/i, /gladiator/i, /gunfighter/i, /virpil/i, /vpc/i,
  /ch\s*(products|pro)/i, /fighterstick/i, /combatstick/i, /flightstick/i,
];

const DEADZONE = 0.1;

// Mouse-aim sensitivity. The base NDC-per-pixel rate lives in aimdirector.js
// (SENS_BASE); the player's Settings slider multiplies it, persisted here.
// This exists because "the plane is hard to steer" turned out to be, in part,
// "my mouse is 400 DPI and the aim barely moves" — a slider is table stakes,
// and War Thunder ships one (its mouse-aim sensitivity scales exactly this:
// how fast the aim direction moves, never the plane's response).
const MOUSE_SENS_KEY = "va-mouse-sens";
export const MOUSE_SENS_MIN = 0.3;
export const MOUSE_SENS_MAX = 3.0;
// The unlocked cursor's rest point sits a hair nose-up to trim out the aim
// depression of the chase-camera ray (it matches the cursor's initial y);
// main.js's path hold and parkCursor both agree on where rest IS.
export const CURSOR_REST_Y = 0.04;

function deadzone(v) {
  const a = Math.abs(v);
  if (a < DEADZONE) return 0;
  return Math.sign(v) * ((a - DEADZONE) / (1 - DEADZONE));
}

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

export class Input {
  constructor(aircraftCfg) {
    this.setRates(aircraftCfg);

    this.pitch = 0;
    this.roll = 0;
    this.yaw = 0;
    this.throttle = 0.7;
    this.trigger = false;
    this.bomb = false;
    this.msl = false; // jets era: heat-seeker launch held (server edge-detects)
    this.flare = false; // jets era: flare dispenser held (server edge-detects)
    this.plotFire = false; // pulsed by the battleship plot-table FIRE button

    // Keyboard smoothed axes (kept separate so they can blend with assist).
    this.kb = { pitch: 0, roll: 0, yaw: 0 };
    // Raw W/S as a drive order (+1 ahead, -1 astern) — see update() for why
    // this exists apart from kb.pitch.
    this.driveAxis = 0;

    // Elevator convention. INVERTED is the shipped default: pull the mouse (or
    // S / Down) back and the nose comes up, the way every stick and yoke a pilot
    // has ever held works. This is a flight sim before it is a shooter — the
    // hands that learn the loop here should be the hands that fly one anywhere
    // else — and the arcade convention it replaces taught the opposite reflex.
    // (`!== "0"` is the same default-on reader mouseAim and autoTrim use: an
    // absent key means ON.) Nothing is taken away — a pilot who set it off keeps
    // that choice, and the I key and the Settings checkbox flip it live, mid-
    // sortie. The touch thumb stick reads this flag too, on both its flight
    // axis and its gun-slew cursor (touch.js): a thumb hauled down the screen
    // is a stick hauled into your lap, in a cockpit or a gun seat. That's why
    // the Settings gear stopped being desktop-only — a phone flies the
    // convention, so a phone has to be able to change it.
    this.invertPitch = localStorage.getItem("va-invert-pitch") !== "0";
    this.mouseAim = localStorage.getItem("va-mouse-aim") !== "0"; // default ON
    // Mouse flight sensitivity multiplier (Settings slider). NaN/absent = 1.
    const sens = parseFloat(localStorage.getItem(MOUSE_SENS_KEY) ?? "1");
    this.mouseSens = Number.isFinite(sens) ? clamp(sens, MOUSE_SENS_MIN, MOUSE_SENS_MAX) : 1;
    this.autoTrim = localStorage.getItem("va-auto-trim") !== "0"; // default ON (T)
    this.autopilot = false; // P: hold straight & level; main.js owns the steering
    this.scoreboard = false;
    this.muteToggled = null;
    this.onToggleMessage = null;
    this.onAutopilot = null; // cb(on) — main.js captures the heading / shows the HUD flag
    this.onAutoTrim = null; // cb(on) — main.js resets the held trim / shows the HUD flag
    this.onDeviceChange = null;
    this.planeRequested = null; // cb(idx) — 1/2/3 while dead
    this.bailRequested = null; // cb() — F: bail from cockpit, gun, hull, or turret
    this.settingsRequested = null; // cb() — O opens Settings mid-sortie (main.js owns the modal)

    // Virtual cursor in NDC-ish coords (x right, y up), radius-limited.
    this.cursor = { x: 0, y: CURSOR_REST_Y };
    this.pointerLocked = false;
    this.hullDrive = false; // main.js: crewing a hull — park the throttle keys

    // Raw pointer-lock mouse motion (pixels, y up) accumulated since the last
    // takeAimDelta(). Turret/gunsight modes consume this for POSITION-control
    // aim (the sight moves exactly as far as the mouse), instead of steering a
    // slew rate off the cursor's deflection — rate control makes the sight
    // sail past the target and forces constant overcorrection.
    this.aimDelta = { x: 0, y: 0 };

    // Assist controls computed by main.js from the cursor (mouse aim).
    this.assist = { pitch: 0, roll: 0, yaw: 0 };

    // TouchControls (touch.js) attaches itself here on coarse-pointer
    // devices; it drives the cursor directly and owns fire/bomb/throttle.
    this.touch = null;

    // Freelook (hold right mouse button): camera orbit angles in radians.
    // blend eases 0→1 while held; angles spring back to center on release.
    this.look = { active: false, yaw: 0, pitch: 0, blend: 0 };

    // Gamepad state.
    this.pad = {
      index: -1,
      kind: null, // "gamepad" | "hotas"
      name: "",
      calib: null,
      active: false, // sticks deflected recently
      pitch: 0,
      roll: 0,
      yaw: 0,
      throttleAbs: null,
      throttleRate: 0,
      fire: false,
      bomb: false,
      lastActivity: 0,
    };
    this.hotasMap = this.loadHotasMap();

    this.keys = new Set();
    this.mouseDown = false;

    addEventListener("keydown", (e) => this.onKey(e, true));
    addEventListener("keyup", (e) => this.onKey(e, false));
    addEventListener("mousedown", (e) => {
      if (this.touch) return; // taps synthesize mouse events; buttons own firing
      if (e.target.closest("#join-screen")) return;
      if (e.button === 0) this.mouseDown = true;
      if (e.button === 2) this.look.active = true;
    });
    addEventListener("mouseup", (e) => {
      if (e.button === 0) this.mouseDown = false;
      if (e.button === 2) this.look.active = false;
    });
    addEventListener("contextmenu", (e) => {
      // Right button is freelook; keep the browser menu off the playfield.
      if (!e.target.closest("#join-screen")) e.preventDefault();
    });
    addEventListener("mousemove", (e) => this.onMouseMove(e));
    document.addEventListener("pointerlockchange", () => {
      this.pointerLocked = document.pointerLockElement !== null;
    });
    addEventListener("blur", () => {
      this.keys.clear();
      this.mouseDown = false;
      this.look.active = false;
      this.scoreboard = false;
    });
    addEventListener("gamepadconnected", (e) => this.adoptGamepad(e.gamepad));
    addEventListener("gamepaddisconnected", (e) => {
      if (e.gamepad.index === this.pad.index) {
        this.pad.index = -1;
        this.pad.kind = null;
        this.pad.active = false;
        this.onDeviceChange?.(null);
      }
    });
  }

  setRates(aircraftCfg) {
    this.slew = aircraftCfg.maxControlRate;
    this.center = aircraftCfg.controlCenterRate;
  }

  loadHotasMap() {
    const def = { roll: 0, pitch: 1, yaw: 2, throttle: 3, trigger: 0, bomb: 1, throttleForwardIsNegative: true };
    try {
      return { ...def, ...JSON.parse(localStorage.getItem("va-hotas-map") ?? "{}") };
    } catch {
      return def;
    }
  }

  adoptGamepad(gp) {
    if (!gp) return;
    this.pad.index = gp.index;
    this.pad.name = gp.id;
    const isHotas = HOTAS_DEVICE_PATTERNS.some((re) => re.test(gp.id));
    this.pad.kind = isHotas || gp.mapping !== "standard" ? "hotas" : "gamepad";
    // Capture resting-position offsets for the steering axes (not throttle).
    this.pad.calib = gp.axes.slice(0, 3).map((v) => (Math.abs(v) < 0.4 ? v : 0));
    this.onDeviceChange?.({ kind: this.pad.kind, name: gp.id });
  }

  pollGamepad() {
    const pad = this.pad;
    if (pad.index < 0) {
      // Late adoption: pick up pads that were connected before page load.
      for (const gp of navigator.getGamepads?.() ?? []) {
        if (gp) {
          this.adoptGamepad(gp);
          break;
        }
      }
      if (pad.index < 0) return;
    }
    const gp = navigator.getGamepads()[pad.index];
    if (!gp) return;

    const cal = (i) => deadzone((gp.axes[i] ?? 0) - (pad.calib?.[i] ?? 0));

    if (pad.kind === "hotas") {
      const m = this.hotasMap;
      pad.roll = cal(m.roll);
      pad.pitch = gp.axes[m.pitch] !== undefined ? deadzone(gp.axes[m.pitch] - (pad.calib?.[m.pitch] ?? 0)) : 0;
      pad.yaw = -cal(m.yaw);
      const raw = gp.axes[m.throttle];
      pad.throttleAbs =
        raw === undefined ? null : m.throttleForwardIsNegative ? (1 - raw) / 2 : (raw + 1) / 2;
      pad.throttleRate = 0;
      pad.fire = !!gp.buttons[m.trigger]?.pressed;
      pad.bomb = !!gp.buttons[m.bomb]?.pressed;
    } else {
      pad.roll = cal(0);
      pad.pitch = cal(1);
      pad.yaw = -deadzone(gp.axes[2] ?? 0);
      pad.throttleAbs = null;
      let rate = -deadzone(gp.axes[3] ?? 0); // push right stick up = spool up
      if (gp.buttons[5]?.pressed) rate += 1; // RB
      if (gp.buttons[4]?.pressed) rate -= 1; // LB
      pad.throttleRate = clamp(rate, -1, 1);
      pad.fire = !!gp.buttons[0]?.pressed || (gp.buttons[7]?.value ?? 0) > 0.4;
      pad.bomb = !!gp.buttons[1]?.pressed;
    }

    const moving =
      Math.abs(pad.roll) + Math.abs(pad.pitch) + Math.abs(pad.yaw) > 0.02 ||
      Math.abs(pad.throttleRate) > 0.02 ||
      pad.fire ||
      pad.bomb;
    const now = performance.now();
    if (moving) pad.lastActivity = now;
    // Sticks "own" the plane while touched and for a moment after — but a
    // thumb on the virtual stick outranks any gamepad. A paired controller
    // with a breath of stick drift keeps `moving` alive forever, and since
    // pad.active both wins the control mix AND gates off the touch
    // instructor (main.js), a phone with a forgotten Bluetooth pad in range
    // reads as "the stick does nothing": the knob follows the thumb while
    // ghost zeros fly the plane. Touch is the most explicit intent there is.
    const touchHeld = this.touch && (this.touch.stick.id !== null || this.touch.thrId !== null);
    pad.active = now - pad.lastActivity < 400 && !touchHeld;
  }

  onMouseMove(e) {
    if (this.touch) return; // the virtual stick owns the cursor on touch
    // Settings modal open: the mouse belongs to the checkboxes, not the stick.
    // Without this, sliding over the panel steers the plane — the keyboard was
    // muzzled by the same latch (see onKey) but the mouse never was, so
    // "opening settings mid-fight" meant flying a hard turn into the scenery.
    if (isCapturing()) return;
    if (this.look.active) {
      // Freelook owns the mouse; the aim cursor stays where it was. The orbit
      // rides the same sensitivity slider as the aim, so one hand fits both.
      const s = 0.0035 * this.mouseSens;
      this.look.yaw = clamp(this.look.yaw + (e.movementX ?? 0) * s, -Math.PI, Math.PI);
      this.look.pitch = clamp(this.look.pitch + (e.movementY ?? 0) * s, -1.2, 1.35);
      return;
    }
    if (this.pointerLocked) {
      // While FLYING these cursor writes are advisory only: the aim director
      // (main.js) owns the reticle each frame from the world-anchored aim and
      // this frame's takeAimDelta() pixels. The accumulation stays for every
      // other cursor consumer (hull conning, the first frame of a mode swap).
      const sens = 0.0022 * this.mouseSens;
      this.cursor.x += e.movementX * sens;
      // Inverted elevator: with invertPitch on (the shipped default), pulling
      // the mouse toward you (movementY > 0, cursor sliding DOWN the screen)
      // raises the aim reticle instead of lowering it — so the plane you're chasing climbs, the
      // AA gun you're rate-aiming elevates, exactly the "pull back to climb" feel
      // the pitch keys already give (see the invertPitch flip in update()). Flip
      // the SIGN of the vertical step, not the reticle after the fact, so the
      // reticle, the plane, and the steer target never disagree.
      //
      // The dividing line is POSITION sights vs RATE sights, not flying vs
      // aiming. This cursor is the rate channel — a virtual joystick, read as
      // deflection-becomes-slew by the manned AA gun, a Leviathan turret, and
      // an unlocked mouse conning a hull — so it follows the stick convention
      // like a stick. aimDelta.y below is the POSITION channel (the
      // pointer-locked naval/turret sight, where the sight moves exactly as far
      // as the hand and mouse-up = sight-up is sacred for laying a shot) and is
      // deliberately left raw. touch.js flips its thumb stick on both counts
      // for the same reason.
      this.cursor.y += (this.invertPitch ? 1 : -1) * e.movementY * sens;
      this.aimDelta.x += e.movementX;
      this.aimDelta.y -= e.movementY;
    } else {
      // No pointer lock yet — the first seconds of a sortie (lock waits on a
      // click), or after Esc released it. The cursor tracks the ABSOLUTE mouse
      // position, and it must honor the same elevator convention as the locked
      // branch above: this path used to skip the flip, so an inverted-elevator
      // pilot flew their first seconds with the OPPOSITE elevator until the
      // lock landed and the controls silently reversed mid-flight.
      this.cursor.x = (e.clientX / innerWidth) * 2 - 1;
      this.cursor.y = (this.invertPitch ? 1 : -1) * ((e.clientY / innerHeight) * 2 - 1);
    }
    // Keep the cursor inside a comfortable steering circle.
    const r = Math.hypot(this.cursor.x, this.cursor.y);
    const maxR = 0.55;
    if (r > maxR) {
      this.cursor.x *= maxR / r;
      this.cursor.y *= maxR / r;
    }
  }

  requestPointerLock(canvas) {
    if (this.mouseAim && !this.pointerLocked) canvas.requestPointerLock?.();
  }

  onKey(e, down) {
    if (e.target.tagName === "INPUT") return;
    // The rebind editor owns the keyboard while it's listening for a key — stand
    // down so binding V doesn't also toggle mouse aim on the way in.
    if (isCapturing()) return;
    const code = e.code;
    const bound = (id) => keymap.matches(id, code);

    // Scoreboard is a hold, not a tap: it never lands in the key set or falls
    // through to the toggles, and it always eats the browser default (Tab-focus).
    if (bound("scoreboard")) {
      this.scoreboard = down;
      e.preventDefault();
      return;
    }
    if (down && bound("invertPitch")) {
      this.invertPitch = !this.invertPitch;
      localStorage.setItem("va-invert-pitch", this.invertPitch ? "1" : "0");
      this.onToggleMessage?.(`elevator ${this.invertPitch ? "inverted — pull back to climb" : "normal — push forward to climb"}`);
    }
    if (down && bound("mouseAim")) {
      this.mouseAim = !this.mouseAim;
      localStorage.setItem("va-mouse-aim", this.mouseAim ? "1" : "0");
      if (!this.mouseAim && document.pointerLockElement) document.exitPointerLock();
      this.onToggleMessage?.(`mouse aim ${this.mouseAim ? "on" : "off"}`);
    }
    if (down && bound("autopilot")) this.toggleAutopilot();
    if (down && bound("autoTrim")) {
      this.autoTrim = !this.autoTrim;
      localStorage.setItem("va-auto-trim", this.autoTrim ? "1" : "0");
      this.onAutoTrim?.(this.autoTrim);
    }
    if (down && bound("mute")) this.muteToggled?.();
    if (down && bound("bail")) this.bailRequested?.();
    if (down && bound("settings")) this.settingsRequested?.();
    if (down && bound("spot")) this.spotRequested?.();
    // Comms wheel: hold the key to open, release to pick the hovered phrase. The
    // rising-edge check reads this.keys BEFORE the add below, so a repeat-fired
    // keydown doesn't re-open it.
    if (bound("comms")) {
      if (down && !this.keys.has(code)) this.commsOpen?.();
      if (!down) this.commsPick?.();
    }
    if (down && /^Digit[1-9]$/.test(code)) {
      this.planeRequested?.(Number(code.slice(-1)) - 1);
    }

    if (down) this.keys.add(code);
    else this.keys.delete(code);

    // Keep the browser from scrolling / focusing things mid-dogfight — but only
    // for keys actually bound to a control (so a rebound throttle on PageUp is
    // swallowed too, and an unbound key still behaves normally on the page).
    if (shouldPreventDefault(code)) e.preventDefault();
  }

  /** True while any physical key bound to this action is held down. */
  held(id) {
    for (const c of keymap.keysFor(id)) if (this.keys.has(c)) return true;
    return false;
  }

  axis(negCodes, posCodes) {
    let v = 0;
    for (const c of negCodes) if (this.keys.has(c)) v -= 1;
    for (const c of posCodes) if (this.keys.has(c)) v += 1;
    return clamp(v, -1, 1);
  }

  /** Flip the autopilot. Shared by the P key and the touch AP button; main.js
   *  decides whether it may engage and owns the steering. */
  toggleAutopilot() {
    this.autopilot = !this.autopilot;
    this.onAutopilot?.(this.autopilot);
  }

  /** Consume the pointer-lock mouse motion accumulated since the last call
   *  (pixels, y up). Position-control aim modes read this once per frame. */
  takeAimDelta() {
    const d = { x: this.aimDelta.x, y: this.aimDelta.y };
    this.aimDelta.x = 0;
    this.aimDelta.y = 0;
    return d;
  }

  /** Set and persist the mouse flight sensitivity multiplier (Settings). */
  setMouseSens(v) {
    this.mouseSens = clamp(v, MOUSE_SENS_MIN, MOUSE_SENS_MAX);
    localStorage.setItem(MOUSE_SENS_KEY, String(this.mouseSens));
  }

  /** Park the aim reticle at its neutral rest. Called on every spawn: without
   *  pointer lock the cursor tracks the ABSOLUTE mouse position, and the last
   *  place the mouse moved before a spawn is the TAKE OFF button — low on the
   *  screen, well inside the steering circle. Left alone, that stale menu
   *  click flies the fresh plane as a hard diving turn until the player
   *  touches the mouse (the e2e hands-off probe augered in from 500 m, every
   *  run, and the first diagnosis blamed the frame rate). A parked reticle
   *  holds the nose steady; the very next real mouse move takes command
   *  exactly as before. */
  parkCursor() {
    this.cursor.x = 0;
    this.cursor.y = CURSOR_REST_Y;
  }

  /** main.js sets this each frame from the cursor steering controller. */
  setAssist(pitch, roll, yaw) {
    this.assist.pitch = pitch;
    this.assist.roll = roll;
    this.assist.yaw = yaw;
  }

  // Fold device + assist + keyboard into the three flight axes. Split out of
  // update() (and dt-free on purpose) so the physics loop can re-fold after
  // refreshing the assist mid-frame: on a slow GPU one frame spans many
  // physics ticks, and a control command computed once from the frame's
  // opening attitude is ancient by the last tick — the low-fps death spiral
  // the e2e hands-off probe measures.
  composeAxes() {
    const pad = this.pad;
    if (pad.active) {
      // Sticks fly the plane; keyboard still adds on top for combos.
      this.pitch = clamp(pad.pitch + this.kb.pitch, -1, 1);
      this.roll = clamp(pad.roll + this.kb.roll, -1, 1);
      this.yaw = clamp(pad.yaw + this.kb.yaw, -1, 1);
    } else if (this.mouseAim) {
      // Mouse steering, with keyboard on top — but the keys OUTRANK the
      // instructor as they deflect. Plain addition let the assist arm-wrestle
      // a held key: press D to roll and the pursuit counter-rolls the whole
      // way because the aim hasn't moved — "the plane fights me". War
      // Thunder's rule (datamined: mouseAimDisableControlTreshold 0.8) is
      // that a manual axis pushed hard simply suspends the instructor on
      // that axis; we run it as a fade so a light tap still trims on top of
      // the aim while a committed key owns the surface outright.
      const ov = (assist, key) => clamp(assist * (1 - Math.min(1, Math.abs(key) / 0.8)) + key, -1, 1);
      this.pitch = ov(this.assist.pitch, this.kb.pitch);
      this.roll = ov(this.assist.roll, this.kb.roll);
      this.yaw = ov(this.assist.yaw, this.kb.yaw);
    } else {
      this.pitch = this.kb.pitch;
      this.roll = this.kb.roll;
      this.yaw = this.kb.yaw;
    }
  }

  update(dt) {
    this.pollGamepad();

    // Freelook blend rises while held; angles spring back after release.
    const lk = this.look;
    if (lk.active) {
      lk.blend = Math.min(1, lk.blend + dt * 7);
    } else if (lk.blend > 0) {
      lk.blend = Math.max(0, lk.blend - dt * 4);
      const d = Math.exp(-9 * dt);
      lk.yaw *= d;
      lk.pitch *= d;
      if (lk.blend === 0) lk.yaw = lk.pitch = 0;
    }

    // The pitch KEYS follow the same elevator convention as the mouse: with
    // invertPitch on (the shipped default — stick-style, pull back / S to climb)
    // they flip to match the reversed mouse elevator in onMouseMove; turn it off
    // and W / Up climbs the arcade way.
    // The key lists come from the live keymap so a rebind takes hold at once.
    let pitchT = this.axis(keymap.keysFor("pitchDown"), keymap.keysFor("pitchUp"));
    if (this.invertPitch) pitchT = -pitchT;
    // The hull drive order reads the same W/S keys as a GEARBOX, not an
    // elevator: W is always "ahead", S is always "brake / astern", whatever
    // way the captain likes their elevator. The flight-model inversion above
    // must stop at the airframe — when the drive slider (main.js) consumed
    // kb.pitch instead of this, invertPitch's default-on flip put every
    // fresh-settings tank and warship in reverse gear on W. That flip is the
    // default again, so this separation is load-bearing, not a leftover.
    this.driveAxis = this.axis(keymap.keysFor("pitchDown"), keymap.keysFor("pitchUp"));
    const rollT = this.axis(keymap.keysFor("rollLeft"), keymap.keysFor("rollRight"));
    // +yaw is nose-left in the flight model, so rudder-left is the positive side.
    const yawT = this.axis(keymap.keysFor("yawRight"), keymap.keysFor("yawLeft"));

    this.kb.pitch = slew(this.kb.pitch, pitchT, dt, this.slew, this.center);
    this.kb.roll = slew(this.kb.roll, rollT, dt, this.slew, this.center);
    this.kb.yaw = slew(this.kb.yaw, yawT, dt, this.slew, this.center);

    this.composeAxes();

    // Throttle: HOTAS lever wins, then the touch slider; otherwise
    // keys/buttons adjust a rate. While driving a hull (main.js sets
    // hullDrive) the key axis is parked: Z/X are the gunsight and shell
    // selector there, and W/S work the drive slider / engine telegraph
    // directly in main.js — without this gate, scoping in (Z) rang the
    // telegraph up and swapping ammo (X) rang it down.
    const pad = this.pad;
    if (pad.active && pad.throttleAbs !== null) {
      this.throttle = pad.throttleAbs;
    } else if (this.touch) {
      this.throttle = this.touch.throttleValue;
    } else if (!this.hullDrive) {
      let thr = this.axis(keymap.keysFor("throttleDown"), keymap.keysFor("throttleUp"));
      if (pad.index >= 0) thr = clamp(thr + pad.throttleRate, -1, 1);
      this.throttle = clamp(this.throttle + thr * dt * 0.55, 0, 1);
    }

    this.trigger =
      this.held("fire") ||
      this.mouseDown ||
      (pad.index >= 0 && pad.fire) ||
      (this.touch?.fire ?? false) ||
      this.plotFire; // battleship plot-table FIRE button
    this.bomb = this.held("bomb") || (pad.index >= 0 && pad.bomb) || (this.touch?.bomb ?? false);
    // Jets era: launch and flare requests. Held state goes on the wire and the
    // server does the edge-detection (the naval consumable pattern).
    this.msl = this.held("missile") || (this.touch?.msl ?? false);
    this.flare = this.held("flare") || (this.touch?.flare ?? false);
  }

  controls() {
    return { pitch: this.pitch, roll: this.roll, yaw: this.yaw, throttle: this.throttle };
  }
}

// Move cur toward target: fast when recentering, measured when deflecting.
function slew(cur, target, dt, rate, centerRate) {
  const r = Math.abs(target) < Math.abs(cur) || target * cur < 0 ? centerRate : rate;
  const maxDelta = r * dt;
  const d = target - cur;
  if (d > maxDelta) return cur + maxDelta;
  if (d < -maxDelta) return cur - maxDelta;
  return target;
}
